// @ts-check
// Diálogo de agendamento (contrato 08 da agenda, seção 5): a partir do card escolhe o tipo (visita, sondagem,
// vivência, prova de bolsa, open school), data, horário, duração e responsável, grava a data do card e manda o
// convite .ics para a família. Também remarca um evento existente e cria compromissos livres na agenda.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { CALENDAR_EVENT_TYPES, OPPORTUNITY_EVENT_TYPES, findCalendarEventType } from '../../../../shared/calendar/event-types.js';
import { createCalendarEvent, scheduleOpportunityEvent, updateCalendarEvent } from '../../data/calendar.js';
import { patchRecordLocally } from '../../data/record-store.js';
import { defaultAssigneeMemberId, loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { combineDateAndTime, toDateKey, toTimeKey } from '../../lib/calendar-dates.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { Modal } from '../../ui/modal.js';
import { Icon } from '../../ui/icon.js';
import { Toggle } from '../../ui/feedback.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/calendar.js').CalendarItem} CalendarItem
 * @typedef {import('../../data/calendar.js').InvitationResult} InvitationResult
 * @typedef {{ id: string, name: string, ownerId?: string | null }} OpportunityTarget
 */

const DURATION_OPTIONS = [30, 45, 60, 90, 120, 180, 240];
const FREE_EVENT_TYPES = CALENDAR_EVENT_TYPES.filter((type) => type.fieldName === null);

const FIRST_BUSINESS_HOUR = 8;
const LAST_BUSINESS_HOUR = 18;

/** Próxima hora cheia dentro do horário comercial; à noite e de madrugada, o dia útil seguinte às 9h. */
function nextFullHour() {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 1);
  if (date.getHours() >= FIRST_BUSINESS_HOUR && date.getHours() < LAST_BUSINESS_HOUR && date.getDay() !== 0) return date;
  if (date.getHours() >= LAST_BUSINESS_HOUR) date.setDate(date.getDate() + 1);
  if (date.getDay() === 0) date.setDate(date.getDate() + 1);
  date.setHours(9);
  return date;
}

/**
 * Aviso do convite depois de salvar.
 * @param {InvitationResult | undefined} invitation
 */
function notifyInvitation(invitation) {
  if (!invitation) return;
  if (invitation.status === 'SENT') showSuccessSnackbar(t('calendar.schedule.invitationSentSnackbar', { recipients: invitation.recipients.join(', ') }));
  else if (invitation.status === 'FAILED') enqueueSnackbar({ message: t('calendar.schedule.invitationFailedSnackbar'), variant: 'error' });
  else if (invitation.reason === 'NO_GUEST_EMAIL') enqueueSnackbar({ message: t('calendar.schedule.invitationSkippedSnackbar'), variant: 'info' });
}

/**
 * @param {{ opportunity?: OpportunityTarget | null, event?: CalendarItem | null, defaultEventType?: string | null, defaultStartsAt?: string | null,
 *   onClose: () => void, onSaved?: (event: CalendarItem) => void }} props
 */
export function ScheduleEventModal({ opportunity = null, event = null, defaultEventType = null, defaultStartsAt = null, onClose, onSaved }) {
  const isReschedule = Boolean(event && event.kind === 'EVENT');
  const linkedOpportunity = opportunity ?? (event?.enrollmentOpportunity ? { id: event.enrollmentOpportunity.id, name: event.enrollmentOpportunity.name, ownerId: event.enrollmentOpportunity.ownerId ?? null } : null);
  const typeOptions = linkedOpportunity ? OPPORTUNITY_EVENT_TYPES : FREE_EVENT_TYPES;
  const initialType = event?.eventType ?? defaultEventType ?? typeOptions[0].key;
  const initialStart = event ? new Date(event.startsAt) : defaultStartsAt ? new Date(defaultStartsAt) : nextFullHour();
  const initialDuration = event ? Math.round((Date.parse(event.endsAt) - Date.parse(event.startsAt)) / 60000) : findCalendarEventType(initialType)?.defaultDurationMinutes ?? 60;
  const [eventType, setEventType] = useState(initialType);
  const [dateKey, setDateKey] = useState(toDateKey(initialStart));
  const [timeKey, setTimeKey] = useState(toTimeKey(initialStart));
  const [durationMinutes, setDurationMinutes] = useState(initialDuration);
  const [title, setTitle] = useState(event && !linkedOpportunity ? event.title ?? '' : '');
  // responsável padrão: o do evento, o dono do card ou quem agenda; na sessão do suporte, ninguém (a instituição
  // convida), porque o membro de suporte não organiza eventos (contrato 08 de cobrança, seção 7.3)
  const [organizerId, setOrganizerId] = useState(
    event?.organizer?.workspaceMemberId ?? linkedOpportunity?.ownerId ?? defaultAssigneeMemberId() ?? '',
  );
  const [location, setLocation] = useState(event?.location ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [sendInvitation, setSendInvitation] = useState(Boolean(linkedOpportunity));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const members = workspaceMembersState.value ?? [];

  useEffect(() => {
    loadWorkspaceMembers();
  }, []);

  const durations = DURATION_OPTIONS.includes(durationMinutes) ? DURATION_OPTIONS : [...DURATION_OPTIONS, durationMinutes].sort((left, right) => left - right);
  const typeLabel = t(`calendar.eventTypes.${eventType}`);
  const modalTitle = isReschedule
    ? t('calendar.schedule.rescheduleTitle', { title: event?.title ?? typeLabel })
    : linkedOpportunity
      ? t('calendar.schedule.titleWithCard', { card: linkedOpportunity.name })
      : t('calendar.schedule.newEventTitle');

  const submit = async () => {
    if (!dateKey || !timeKey || (!linkedOpportunity && !isReschedule && !title.trim())) {
      setError(t('calendar.schedule.required'));
      return;
    }
    setError(null);
    setIsSaving(true);
    const startsAt = combineDateAndTime(dateKey, timeKey);
    const common = {
      startsAt,
      durationMinutes,
      organizerWorkspaceMemberId: organizerId || null,
      location: location.trim() || null,
      description: description.trim() || null,
    };
    try {
      /** @type {{ event: CalendarItem, invitation?: InvitationResult, isRescheduled?: boolean }} */
      let result;
      if (isReschedule && event) {
        result = await updateCalendarEvent(event.id, { ...common, endsAt: new Date(Date.parse(startsAt) + durationMinutes * 60000).toISOString(), sendInvitation });
        showSuccessSnackbar(t('calendar.schedule.rescheduledSnackbar', { type: typeLabel }));
      } else if (linkedOpportunity) {
        result = await scheduleOpportunityEvent({ enrollmentOpportunityId: linkedOpportunity.id, eventType, ...common, sendInvitation });
        showSuccessSnackbar(t(result.isRescheduled ? 'calendar.schedule.rescheduledSnackbar' : 'calendar.schedule.scheduledSnackbar', { type: typeLabel }));
      } else {
        result = await createCalendarEvent({ eventType, title: title.trim(), ...common });
        showSuccessSnackbar(t('calendar.schedule.createdSnackbar'));
      }
      notifyInvitation(result.invitation);
      // a data do card muda junto (tabela, kanban e página do card abertos)
      const savedType = findCalendarEventType(result.event.eventType);
      if (result.event.enrollmentOpportunity && savedType?.fieldName) {
        patchRecordLocally('enrollmentOpportunity', result.event.enrollmentOpportunity.id, { [savedType.fieldName]: result.event.isCanceled ? null : result.event.startsAt });
      }
      onSaved?.(result.event);
      onClose();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal}
    title=${modalTitle}
    onClose=${onClose}
    onConfirm=${submit}
    confirmLabel=${isReschedule ? t('calendar.schedule.confirmReschedule') : linkedOpportunity ? t('calendar.schedule.confirm') : t('calendar.schedule.confirmCreate')}
    isLoading=${isSaving}
  >
    <div class="schedule-form" data-testid="schedule-event-form">
      ${!isReschedule
        ? html`<div class="form-field">
            <span class="form-field-label"><${Icon} name="IconCalendarEvent" size=${14} /><span>${t('calendar.schedule.eventType')}</span></span>
            <div class="schedule-type-options" role="radiogroup" aria-label=${t('calendar.schedule.eventType')}>
              ${typeOptions.map((type) => html`<button
                type="button"
                key=${type.key}
                role="radio"
                class="schedule-type-option"
                aria-checked=${eventType === type.key ? 'true' : 'false'}
                data-color=${type.color}
                onClick=${() => {
                  setEventType(type.key);
                  setDurationMinutes(type.defaultDurationMinutes);
                }}
              ><${Icon} name=${type.icon} size=${14} /><span>${t(`calendar.eventTypes.${type.key}`)}</span></button>`)}
            </div>
          </div>`
        : null}
      ${!linkedOpportunity && !isReschedule
        ? html`<label class="form-field">
            <span class="form-field-label"><${Icon} name="IconTypography" size=${14} /><span>${t('calendar.schedule.eventTitle')}</span></span>
            <input class="text-input" value=${title} placeholder=${t('calendar.schedule.eventTitlePlaceholder')}
              onInput=${(inputEvent) => setTitle(/** @type {HTMLInputElement} */ (inputEvent.currentTarget).value)} />
          </label>`
        : null}
      <div class="form-field-row">
        <label class="form-field">
          <span class="form-field-label"><${Icon} name="IconCalendar" size=${14} /><span>${t('calendar.schedule.date')}</span></span>
          <input class="text-input" type="date" value=${dateKey} required onInput=${(inputEvent) => setDateKey(/** @type {HTMLInputElement} */ (inputEvent.currentTarget).value)} />
        </label>
        <label class="form-field">
          <span class="form-field-label"><${Icon} name="IconClock" size=${14} /><span>${t('calendar.schedule.time')}</span></span>
          <input class="text-input" type="time" step="900" value=${timeKey} required onInput=${(inputEvent) => setTimeKey(/** @type {HTMLInputElement} */ (inputEvent.currentTarget).value)} />
        </label>
      </div>
      <div class="form-field-row">
        <label class="form-field">
          <span class="form-field-label"><${Icon} name="IconHourglass" size=${14} /><span>${t('calendar.schedule.duration')}</span></span>
          <select class="text-input" value=${String(durationMinutes)} onChange=${(inputEvent) => setDurationMinutes(Number(/** @type {HTMLSelectElement} */ (inputEvent.currentTarget).value))}>
            ${durations.map((minutes) => html`<option key=${minutes} value=${String(minutes)}>${t(`calendar.schedule.durations.${minutes}`) === `calendar.schedule.durations.${minutes}` ? `${minutes} min` : t(`calendar.schedule.durations.${minutes}`)}</option>`)}
          </select>
        </label>
        <label class="form-field">
          <span class="form-field-label"><${Icon} name="IconUserCircle" size=${14} /><span>${t('calendar.schedule.owner')}</span></span>
          <select class="text-input" value=${organizerId} onChange=${(inputEvent) => setOrganizerId(/** @type {HTMLSelectElement} */ (inputEvent.currentTarget).value)}>
            <option value="">${t('calendar.schedule.noOwner')}</option>
            ${members.map((member) => html`<option key=${member.id} value=${member.id}>${formatFullName(member.name) || member.userEmail || member.id}</option>`)}
            ${organizerId && !members.some((member) => member.id === organizerId) && event?.organizer
              ? html`<option value=${organizerId}>${event.organizer.name}</option>`
              : null}
          </select>
        </label>
      </div>
      <label class="form-field">
        <span class="form-field-label"><${Icon} name="IconMapPin" size=${14} /><span>${t('calendar.schedule.location')}</span></span>
        <input class="text-input" value=${location} placeholder=${t('calendar.schedule.locationPlaceholder')}
          onInput=${(inputEvent) => setLocation(/** @type {HTMLInputElement} */ (inputEvent.currentTarget).value)} />
      </label>
      <label class="form-field">
        <span class="form-field-label"><${Icon} name="IconAlignLeft" size=${14} /><span>${t('calendar.schedule.notes')}</span></span>
        <textarea class="text-input schedule-notes" rows="2" value=${description} placeholder=${t('calendar.schedule.notesPlaceholder')}
          onInput=${(inputEvent) => setDescription(/** @type {HTMLTextAreaElement} */ (inputEvent.currentTarget).value)}></textarea>
      </label>
      ${linkedOpportunity
        ? html`<div class="schedule-invitation">
            <div class="schedule-invitation-texts">
              <span class="schedule-invitation-title">${t('calendar.schedule.sendInvitation')}</span>
              <span class="input-hint">${t('calendar.schedule.invitationHint')}</span>
            </div>
            <${Toggle} value=${sendInvitation} onChange=${setSendInvitation} ariaLabel=${t('calendar.schedule.sendInvitation')} />
          </div>`
        : null}
      ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    </div>
  <//>`;
}
